import React, { useEffect, useState } from "react";
import Loader from "../../../Components/GlobalLoader/Loader";
import CommonModal from "../../../Components/Modals/CommonModal";
import Table from "../../../Components/Tables/Table";
import ExportLayout from "../../../Layout/ExportLayout";
import ImportLayout from "../../../Layout/ImportLayout";
import PageHeader from "../../../Layout/PageHeader";
import Tabs from "../../../Shared/Tab/Tab";
import { Tab } from "../../../Shared/Tab/TabProps";
import { usePagination } from "../../../hooks/usePagination";
import { useSorting } from "../../../hooks/useSorting";
import { ApiResponse } from "../../../model/common/apiResponse";
import { useSearchStore } from "../../../store/search/searchStore";
import {
  fetchAsnData,
  fetchStatusWiseCounts,
} from "../service/asnOverview.service";
import { ActiveTab, AsnListProps } from "../model/AsnList.type";
import { buildFilterConditions, formatSearchDate, isValidDate } from "../../../Utility/Utility";

const AsnList: React.FC<AsnListProps> = ({
  columns,
  db_columns,
  pageName,
  addUrl,
  listUrl,
  Offcanvas,
  pageTabs,
  handleGlobalFilter,
  buttonStatus,
  isCreateBtnDisable = false,
  checkboxStatus = true,
  search_columns,
  advancedFilterData=null,
  filterDataCount,
  advanceFilterMapper,
  warehouseId,
  clientId,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    order: pageTabs[0]?.order ?? 0,
    name: pageTabs[0]?.name ?? "",
  });
  const { currentPage, handlePageChange, perPage } = usePagination();
  const [showModalForImportAndExport, setShowModalForImportAndExport] =
    useState({
      importModal: false,
      exportModal: false,
    });
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedRow, setSelectedRow] = useState<number>();
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(!show);
  const [data, setData] = useState<ApiResponse<any> | null>(null);
  const statuses = pageTabs.map((tab) => tab.name);
  const [tabRecordcount, setTabRecordcount] = useState<{
    [key: string]: number;
  }>(
    statuses.reduce((acc, status) => {
      acc[status] = 0; // Initialize each status count to 0
      return acc;
    }, {} as { [key: string]: number })
  );


  const { sortingData, handleSortChange } = useSorting(handlePageChange);

  const { searchTerm, resetSearch, previousSearchTerm, setPreviousTerm } =
    useSearchStore();
  useEffect(() => {
    if (searchTerm !== previousSearchTerm && currentPage !== 1) {
      handlePageChange(1);
    }

    let order: any[] = [];
    if (sortingData.column.column_name && sortingData.direction) {
      order = [
        {
          column: sortingData.column.column_name,
          dir: sortingData.direction,
        },
      ];
    }
    let conditions: any[] = [
      ["inbound_shipment.status", "=", activeTab.name]
    ]
    let filterPayload: any[] = [];
    if (advancedFilterData?.supplier) {
      filterPayload = buildFilterConditions(advancedFilterData,advanceFilterMapper);
    }
    if (warehouseId) {
      filterPayload.push(["warehouse.id", "=", warehouseId]);
    }
    if (clientId) {
      filterPayload.push(["client.id", "=", clientId]);
    }
    conditions = conditions.concat(filterPayload);
      const formattedSearch = isValidDate(searchTerm) ? formatSearchDate(searchTerm) : searchTerm;
    
    const payload: any = {
      columns: db_columns,
      search_columns:search_columns,
      conditions: conditions,

      limit: perPage,
      page: currentPage,
      search: formattedSearch,
      order: [
        {
          column: "inbound_shipment.id",
          dir: "desc",
        },
      ],
    };
    if (order.length > 0) {
      Object.assign(payload, { order });
    }
    // set data to null
    setData(null);
    fetchStatusWiseCounts(statuses as string[],formattedSearch,filterPayload,search_columns)
    
      .then((res: any) => {
        if (res?.data) {
          setTabRecordcount((prevState) => {
            const updatedCounts: { [key: string]: number } = {
              ...prevState,
              [activeTab.name]: res.data[activeTab.name] || 0,
            };

            // Loop through statuses to dynamically set counts
            statuses.forEach((status) => {
              updatedCounts[status] = res.data[status] || 0; // Set count for each status
            });
            return updatedCounts;
          });
        }
      })
      .then(() => {
        fetchAsnData(currentPage, payload).then((response) => {
          setData(response ?? null);
          setPreviousTerm(searchTerm);
        });
      })
      .catch((error) => {
        console.error("Error fetching status-wise counts:", error);
      });
  }, [currentPage, activeTab, searchTerm, sortingData,warehouseId,clientId,advancedFilterData]);

  const handleSelectedRowsChange = (newSelectedRows: Set<number>) => {
    const row_no = Array.from(newSelectedRows)[0];
    setSelectedRow(data?.data?.results[row_no]?.id);
    setSelectedRows(newSelectedRows);
  };

  const onTabClick = (tab: Tab) => {
    setActiveTab({ order: tab.order ?? 0, name: tab.name ?? "new" });
    handlePageChange(1);
  };

  const numberOfLinesEqZero = (row: any) => {
    return row.no_of_lines === 0; 
  };

  const renderTable = (multiSelect?: boolean | null) => {
    return (
      <Table
      customClassName="sph-table-color"
        enableSorting={true}
        columns={columns}
        data={data?.data?.results || []}
        pagination={{
          recordsPerPage: perPage,
          totalRecords: data?.data?.recordsTotal,
          currentPage: currentPage,
          onCurrentPageChange: handlePageChange,
        }}
        multiselect={multiSelect}
        onSortChange={handleSortChange}
        sortingData={sortingData}
        onSelectedRowsChange={handleSelectedRowsChange}
        CheckboxStatus={checkboxStatus}
        checkboxDisableCondition={numberOfLinesEqZero}
      />
    );
  };

  const tabs = pageTabs.map((tab) => ({
    ...tab,
    label: `${tab.tabLable} | ${tabRecordcount[tab.name] ?? 0}`,
    content: renderTable(tab.multiSelect),
  }));

  return data ? (
    <>
      <PageHeader
        addUrl={selectedRow?addUrl+'/'+selectedRow:addUrl}
        listUrl={listUrl}
        name={pageName}
        nameButtonStatus={
          isCreateBtnDisable ? selectedRows.size > 0 : buttonStatus
        }
        handleGlobalFilter={handleGlobalFilter}
        // handleGlobalSearch={handleGlobalSearch}
        setShowModalForImportAndExport={setShowModalForImportAndExport}
        showModalForImportAndExport={showModalForImportAndExport}
        filterDataCount = {filterDataCount}
      />

      <div className="clearfix"></div>

      <h5 className="text-start mt-1 mb-3">{pageName}</h5>

      <div>
        <Tabs
          tabs={tabs}
          isCheckbox={true}
          onTabClick={onTabClick}
          active={activeTab.order}
        />
      </div>
      <div className="inbound-CheckIn-Table-Main-Div"></div>

      {Offcanvas}

      <CommonModal
        show={showModalForImportAndExport.importModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, importModal: false };
          })
        }
        heading={"Import File"}
        content={<ImportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
      <CommonModal
        show={showModalForImportAndExport.exportModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, exportModal: false };
          })
        }
        heading={"Export"}
        content={<ExportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
    </>
  ) : (
    <Loader />
  );
};
export default AsnList;
